/**
 * UID-458 — what the cart row's "Edit configuration" action means for THIS
 * line, decided from the one discriminator every cart line is guaranteed to
 * carry: its `SourceableRef.kind` (a REQUIRED contract field — catalog /
 * configurable / custom / assembly_bom).
 *
 * Before this module the row's kebab menu opened the Custom-parts
 * configurator shell (`PartConfiguratorShell`, wired via
 * `cart-line-configurator.ts`) for EVERY line, regardless of kind — so a
 * standard catalog bearing was offered a process/material/finish editor it
 * has no business having, the exact "never invite an edit the system
 * discards" failure mode UID-403 exists not to reintroduce.
 *
 * Deliberately NOT placed in `cart-line-configurator.ts`: that module is the
 * Custom-parts adapter (it owns `CART_CONFIGURATOR_CAPABILITIES` /
 * `fromCartLine`) and must stay ignorant of the other kinds. Deliberately
 * NOT a new `PartConfiguratorCapabilities` variant either — the architecture
 * review for this ticket ruled the shared shell stays the custom-spec path
 * only.
 */
import type { components } from '../../lib/api/schema.gen'

type LineItem = components['schemas']['LineItem']

/** Why a standard catalog part has no configuration to edit: it is a
 * finished, stocked SKU — its specification IS the SKU. */
export const CATALOG_NOT_CONFIGURABLE_REASON =
  "This is a standard catalog part — there's nothing to configure. Remove it and add a different part number instead."

/** UID-459 — a `configurable` catalog part genuinely HAS configuration
 * options; the cart just can't edit them yet (the Catalog's own
 * configuration UI owns those picks, and re-seeding it from a cart line is
 * that ticket's work). Distinct copy on purpose: reusing the standard-catalog
 * reason above would falsely claim there is nothing to configure. */
export const CONFIGURABLE_NOT_EDITABLE_IN_CART_REASON =
  "This part's configuration can't be changed from the cart yet — change configuration options from the Catalog and re-add this item to the cart."

/** An assembly line's "configuration" is its bill of materials, so there is
 * no single-line specification to edit here. */
export const ASSEMBLY_BOM_NOT_EDITABLE_REASON =
  "This item's configuration is defined by its assembly and can't be edited individually."

export type CartLineEditAction =
  /** Open the shared configurator shell on the line's own persisted
   * `CartLineSpecification` (today's behavior, now scoped to custom parts). */
  | { readonly kind: 'custom-spec' }
  /** Reserved for UID-459 (reopen the real Catalog configuration UI,
   * pre-seeded with the line's current picks). Not produced by
   * {@link resolveCartLineEditAction} yet — `configurable` resolves to
   * `unavailable` with its own reason until that ticket lands. */
  | { readonly kind: 'catalog-configurable' }
  /** The action renders, but disabled, with `reason` shown to every user
   * (including assistive tech — see `CartLineRow`'s `RowMenu`). */
  | { readonly kind: 'unavailable'; readonly reason: string }

/** Compile-time exhaustiveness guard: only callable with a value TypeScript
 * has already narrowed to `never`. Reachable at runtime only if a value
 * outside the contract's closed `SourceableKind` set arrives over the wire,
 * in which case throwing is the honest outcome — better than guessing an
 * edit affordance for a kind this build has never heard of. */
export function assertNever(value: never): never {
  throw new Error(`Unhandled SourceableKind: ${String(value)}`)
}

/**
 * Exhaustive over `SourceableKind` with NO `default` case, on purpose: the
 * trailing {@link assertNever} only typechecks while every kind is handled
 * above it, so adding a fifth kind to the contract (or deleting a case here)
 * FAILS THE BUILD rather than silently routing an unknown kind into the
 * custom-parts configurator.
 */
export function resolveCartLineEditAction(line: LineItem): CartLineEditAction {
  const kind = line.sourceable.kind
  switch (kind) {
    case 'custom':
      return { kind: 'custom-spec' }
    case 'catalog':
      return { kind: 'unavailable', reason: CATALOG_NOT_CONFIGURABLE_REASON }
    case 'configurable':
      return { kind: 'unavailable', reason: CONFIGURABLE_NOT_EDITABLE_IN_CART_REASON }
    case 'assembly_bom':
      return { kind: 'unavailable', reason: ASSEMBLY_BOM_NOT_EDITABLE_REASON }
  }
  return assertNever(kind)
}
